import { applyI18n, t } from '../i18n';
import { iconImg } from './icons';

/** Matches SEDR / files mobile layout breakpoint. */
export const FILES_MOBILE_LAYOUT_MQ = '(max-width: 768px)';

export interface FilesBrowseMobileTreeHandle {
  dispose: () => void;
  closeTree: () => void;
  openTree: () => void;
  isMobile: () => boolean;
  isTreeOpen: () => boolean;
}

export interface BindFilesBrowseMobileTreeOptions {
  /** `.files-browse-split` or `.sedr-file-picker` root. */
  root: HTMLElement;
  /** Explorer toolbar that hosts the Folders toggle. */
  explorerBar: HTMLElement;
}

/**
 * On narrow viewports, hide the directory tree (+ search) by default.
 * The Folders control opens them in a bottom sheet over the file list.
 * Selecting a folder should call `closeTree()`.
 */
export function bindFilesBrowseMobileTree(
  options: BindFilesBrowseMobileTreeOptions,
): FilesBrowseMobileTreeHandle {
  const { root, explorerBar } = options;
  const mq = window.matchMedia(FILES_MOBILE_LAYOUT_MQ);
  const actions = explorerBar.querySelector<HTMLElement>('.files-explorer-bar__actions');
  const treePane = root.querySelector<HTMLElement>('.files-browse-tree-pane');

  let toggle = explorerBar.querySelector<HTMLButtonElement>('.files-browse-folders-toggle');
  if (!toggle) {
    toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'icon-button files-browse-folders-toggle';
    toggle.setAttribute('data-i18n-aria-label', 'files.browse.foldersToggle');
    toggle.setAttribute('data-i18n-title', 'files.browse.foldersToggle');
    toggle.append(iconImg('files', 16));
    // Place as a sibling before the actions container so rebuilds of + / upload
    // (actions.innerHTML = ...) do not remove the folders control.
    if (actions) {
      actions.before(toggle);
    } else {
      explorerBar.append(toggle);
    }
  }
  applyI18n(toggle);

  let backdrop: HTMLButtonElement | null = null;
  let sheetHeader: HTMLElement | null = null;

  const ensureSheetChrome = () => {
    if (!treePane) return;

    if (!sheetHeader) {
      sheetHeader = document.createElement('div');
      sheetHeader.className = 'files-browse-mobile-sheet__header';
      const title = document.createElement('span');
      title.className = 'files-browse-mobile-sheet__title';
      title.setAttribute('data-i18n', 'files.browse.foldersSheetTitle');
      title.textContent = t('files.browse.foldersSheetTitle');
      const closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'icon-button files-browse-mobile-sheet__close';
      closeBtn.setAttribute('data-i18n-aria-label', 'common.close');
      closeBtn.setAttribute('data-i18n-title', 'common.close');
      closeBtn.append(iconImg('x', 16));
      closeBtn.addEventListener('click', () => setTreeOpen(false));
      sheetHeader.append(title, closeBtn);
      treePane.prepend(sheetHeader);
      applyI18n(sheetHeader);
    }

    if (!backdrop) {
      backdrop = document.createElement('button');
      backdrop.type = 'button';
      backdrop.className = 'files-browse-mobile-backdrop';
      backdrop.setAttribute('data-i18n-aria-label', 'files.browse.foldersSheetDismiss');
      backdrop.setAttribute('aria-label', t('files.browse.foldersSheetDismiss'));
      backdrop.addEventListener('click', () => setTreeOpen(false));
      root.append(backdrop);
      applyI18n(backdrop);
    }
  };

  const clearSheetChrome = () => {
    backdrop?.remove();
    backdrop = null;
    // Keep header in the pane so reopen is cheap; hide via CSS when sheet closed.
  };

  const setTreeOpen = (open: boolean) => {
    root.classList.toggle('files-browse-mobile--tree-open', open);
    toggle!.setAttribute('aria-expanded', open ? 'true' : 'false');
    toggle!.classList.toggle('files-browse-folders-toggle--active', open);
    if (open && mq.matches) {
      ensureSheetChrome();
    } else {
      clearSheetChrome();
    }
  };

  const syncMobileClass = () => {
    const mobile = mq.matches;
    root.classList.toggle('files-browse-mobile', mobile);
    toggle!.hidden = !mobile;
    if (!mobile) {
      setTreeOpen(false);
      sheetHeader?.remove();
      sheetHeader = null;
    } else if (!root.classList.contains('files-browse-mobile--tree-open')) {
      setTreeOpen(false);
    }
  };

  const onToggleClick = () => {
    if (!mq.matches) return;
    setTreeOpen(!root.classList.contains('files-browse-mobile--tree-open'));
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && root.classList.contains('files-browse-mobile--tree-open')) {
      event.preventDefault();
      setTreeOpen(false);
    }
  };

  toggle.addEventListener('click', onToggleClick);
  mq.addEventListener('change', syncMobileClass);
  window.addEventListener('keydown', onKeyDown);
  syncMobileClass();

  return {
    dispose: () => {
      toggle?.removeEventListener('click', onToggleClick);
      mq.removeEventListener('change', syncMobileClass);
      window.removeEventListener('keydown', onKeyDown);
      clearSheetChrome();
      sheetHeader?.remove();
      sheetHeader = null;
      root.classList.remove('files-browse-mobile', 'files-browse-mobile--tree-open');
      toggle?.remove();
    },
    closeTree: () => setTreeOpen(false),
    openTree: () => {
      if (mq.matches) setTreeOpen(true);
    },
    isMobile: () => mq.matches,
    isTreeOpen: () => root.classList.contains('files-browse-mobile--tree-open'),
  };
}
